import React, { useState, useEffect, useCallback } from 'react';
import { candidatesAPI, getErrorMessage } from '../services/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import type { Candidate, CandidateDetail, CreateCandidateRequest, CandidateStatus } from '../types';

interface CandidatesTabProps {
  jobId: number;
}

function CandidatesTab({ jobId }: CandidatesTabProps) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateDetail | null>(null);
  const [formData, setFormData] = useState<CreateCandidateRequest>({
    name: '',
    email: '',
    phone: '',
  });

  const loadCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await candidatesAPI.getAll(jobId);
      setCandidates(data.candidates || []);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const handleCreate = () => {
    setFormData({ name: '', email: '', phone: '' });
    setShowModal(true);
  };

  const handleViewDetails = async (candidate: Candidate) => {
    try {
      const details = await candidatesAPI.getById(jobId, candidate.id);
      setSelectedCandidate(details);
      setShowDetailModal(true);
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to load candidate details');
    }
  };

  const handleDelete = async (candidateId: number) => {
    if (!window.confirm('Are you sure you want to delete this candidate? All associated data including video responses will be permanently deleted.')) {
      return;
    }

    try {
      await candidatesAPI.delete(jobId, candidateId);
      loadCandidates();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to delete candidate');
    }
  };

  const handleSendInvitation = async (candidateId: number) => {
    try {
      const result = await candidatesAPI.sendInvitation(jobId, candidateId);
      alert(`Invitation sent!\n\nInterview Link:\n${result.interview_link}\n\nYou can share this link with the candidate.`);
      loadCandidates();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to send invitation');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await candidatesAPI.create(jobId, formData);
      setShowModal(false);
      loadCandidates();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to create candidate');
    }
  };

  const getStatusBadgeVariant = (status: CandidateStatus): 'warning' | 'info' | 'success' => {
    const statusMap: Record<CandidateStatus, 'warning' | 'info' | 'success'> = {
      pending: 'warning',
      invited: 'info',
      in_progress: 'info',
      completed: 'success',
      analyzed: 'success',
    };
    return statusMap[status];
  };

  if (loading) {
    return <div className="text-center py-10 text-gray-600">Loading candidates...</div>;
  }

  if (error) {
    return <div className="bg-danger-light text-danger-text px-4 py-3 rounded-md">{error}</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div className="grid grid-cols-3 gap-5 flex-1">
          <Card className="p-5">
            <h4 className="text-sm uppercase tracking-wider text-gray-600 mb-2">Total</h4>
            <p className="text-[32px] font-bold text-dark m-0">{candidates.length}</p>
          </Card>
          <Card className="p-5">
            <h4 className="text-sm uppercase tracking-wider text-gray-600 mb-2">Pending</h4>
            <p className="text-[32px] font-bold text-dark m-0">{candidates.filter(c => c.status === 'pending').length}</p>
          </Card>
          <Card className="p-5">
            <h4 className="text-sm uppercase tracking-wider text-gray-600 mb-2">Completed</h4>
            <p className="text-[32px] font-bold text-dark m-0">{candidates.filter(c => c.status === 'completed').length}</p>
          </Card>
        </div>
        <Button onClick={handleCreate} className="ml-5">
          + Add Candidate
        </Button>
      </div>

      {candidates.length === 0 ? (
        <div className="text-center py-16">
          <h3 className="text-lg font-medium text-gray-700 mb-2">No candidates found</h3>
          <p className="text-gray-500 mb-5">Add candidates for this job</p>
          <Button onClick={handleCreate}>
            + Add Candidate
          </Button>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-32">Phone</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-32">Created At</TableHead>
                <TableHead className="w-72">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {candidates.map(candidate => (
                <TableRow key={candidate.id}>
                  <TableCell>{candidate.id}</TableCell>
                  <TableCell className="font-semibold">{candidate.name}</TableCell>
                  <TableCell>{candidate.email}</TableCell>
                  <TableCell>{candidate.phone || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(candidate.status)}>
                      {candidate.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(candidate.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => handleViewDetails(candidate)}>
                        View
                      </Button>
                      {(candidate.status === 'pending' || !candidate.access_token) && (
                        <Button size="sm" variant="success" onClick={() => handleSendInvitation(candidate.id)}>
                          Invite
                        </Button>
                      )}
                      <Button size="sm" variant="danger" onClick={() => handleDelete(candidate.id)}>
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add Candidate Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Candidate</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="e.g., john@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g., +1234567890"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Add Candidate
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Candidate Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Candidate Details: {selectedCandidate?.name}</DialogTitle>
          </DialogHeader>
          {selectedCandidate && (
            <div className="space-y-5">
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4">
                  <h4 className="text-xs uppercase tracking-wider text-gray-600 mb-2">Email</h4>
                  <p className="text-sm text-dark break-words">{selectedCandidate.email}</p>
                </Card>
                <Card className="p-4">
                  <h4 className="text-xs uppercase tracking-wider text-gray-600 mb-2">Phone</h4>
                  <p className="text-sm text-dark">{selectedCandidate.phone || 'N/A'}</p>
                </Card>
                <Card className="p-4">
                  <h4 className="text-xs uppercase tracking-wider text-gray-600 mb-2">Status</h4>
                  <Badge variant={getStatusBadgeVariant(selectedCandidate.status)}>
                    {selectedCandidate.status}
                  </Badge>
                </Card>
                <Card className="p-4">
                  <h4 className="text-xs uppercase tracking-wider text-gray-600 mb-2">Responses</h4>
                  <p className="text-2xl font-bold text-dark m-0">{selectedCandidate.responses_count || 0}</p>
                </Card>
              </div>

              {selectedCandidate.ai_score !== null && (
                <Card className="p-5">
                  <h4 className="text-base font-semibold text-dark mb-4">AI Analysis</h4>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-4">
                      <h4 className="text-xs uppercase tracking-wider text-gray-600 mb-2">Score</h4>
                      <p className="text-2xl font-bold text-dark m-0">{selectedCandidate.ai_score}/100</p>
                    </Card>
                    <Card className="p-4">
                      <h4 className="text-xs uppercase tracking-wider text-gray-600 mb-2">Ranking</h4>
                      <p className="text-2xl font-bold text-dark m-0">#{selectedCandidate.ai_ranking || 'N/A'}</p>
                    </Card>
                  </div>
                  {selectedCandidate.ai_summary && (
                    <div>
                      <strong className="text-dark">Summary:</strong>
                      <p className="mt-2 leading-relaxed text-gray-700">{selectedCandidate.ai_summary}</p>
                    </div>
                  )}
                </Card>
              )}

              {selectedCandidate.responses && selectedCandidate.responses.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-dark mb-3">Responses ({selectedCandidate.responses.length})</h4>
                  <div className="space-y-3">
                    {selectedCandidate.responses.map((response, idx) => (
                      <Card key={response.id} className="p-4">
                        <h5 className="text-sm font-semibold text-dark mb-2">Question {idx + 1}</h5>
                        <p className="font-medium text-dark mb-3">{response.question_text || 'Question text not available'}</p>
                        {response.summary && (
                          <div className="mt-3 p-3 bg-gray-100 rounded-md">
                            <strong className="text-sm text-dark">AI Summary:</strong>
                            <p className="mt-1 text-sm text-gray-700">{response.summary}</p>
                          </div>
                        )}
                        {response.sentiment_score !== null && (
                          <p className="mt-2 text-sm text-gray-700">
                            <strong>Sentiment Score:</strong> {response.sentiment_score.toFixed(2)}
                          </p>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CandidatesTab;
